/**
 * useAutoSSO Hook
 * Hook React que orquesta el auto-login SSO mediante redirect
 * Principio: Separation of Concerns - Lógica de React separada del servicio
 * 
 * IMPORTANTE: Este hook solo INICIA el redirect al SSO.
 * El callback de retorno debe manejarse en otra parte de la aplicación.
 */

import { useEffect, useRef } from 'react';
import { SSOService } from '../services/sso/SSOService';
import { 
  processSSOErrors, 
  isAutoSSODisabledForSession,
  disableAutoSSOForSession 
} from '../services/sso/SSOErrorHandler';

/**
 * Hook para intentar auto-login SSO al montar el componente
 * Si hay referer válido, redirige automáticamente al SSO
 * @param {Object} options - Opciones del hook
 * @param {boolean} options.enabled - Si el hook está habilitado (default: true)
 * @returns {null}
 */
export default function useAutoSSO({
  enabled = true,
} = {}) {
  const attemptedRef = useRef(false);

  useEffect(() => {
    // Solo intentar una vez por sesión del componente
    if (!enabled || attemptedRef.current) {
      return;
    }

    attemptedRef.current = true;

    const performSSO = async () => {
      console.log('[useAutoSSO] 🚀 Checking for SSO auto-login...');

      try {
        // 1. Verificar si hay errores SSO en la URL
        const ssoErrorResult = processSSOErrors();
        
        if (ssoErrorResult.hasError) {
          console.log('[useAutoSSO] ❌ SSO error detected:', ssoErrorResult.errorData);
          
          // Si se debe bloquear SSO, deshabilitarlo para la sesión
          if (ssoErrorResult.shouldBlockSSO) {
            disableAutoSSOForSession();
            console.log('[useAutoSSO] 🚫 SSO auto-login disabled due to error');
            return;
          }
        }

        // 2. Verificar si SSO automático está deshabilitado para la sesión
        if (isAutoSSODisabledForSession()) {
          console.log('[useAutoSSO] ⏸️ SSO auto-login disabled for this session');
          return;
        }

        // 3. Intentar SSO automático
        console.log('[useAutoSSO] 🔄 Attempting SSO auto-login...');
        
        // Intenta SSO - si hay referer válido, hará redirect automático
        const result = await SSOService.attemptAutoLogin();

        // Este código solo se ejecuta si NO hay referer (no redirige)
        if (!result.success) {
          console.log(`[useAutoSSO] ⚠️ SSO not attempted: ${result.message}`);
        }
        // Si result.success = true, significa que está redirigiendo (no se ejecutará más código)

      } catch (error) {
        console.error('[useAutoSSO] ❌ Error during SSO:', error);
      }
    };

    performSSO();
  }, [enabled]);

  // No retorna estado porque el redirect sale de la app
  return null;
}
